import "./polyfills";
import "./sentry";
import { errorCode, httpStatus } from "@utils/errors";
import React from "react";

import { toast } from "@UI/components/Molecules/Notification/toast.ts";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import LogRocket from "logrocket";

import "./assets/styles/index.css";
import "@UI/assets/index.scss";

import "@services/axios";
import "@services/i18n";

import * as serviceWorker from "./serviceWorker";
import App from "./App";
import { ValidationError } from "yup";
import { createRoot } from "react-dom/client";
import { TourTriggerContextProvider } from "@context/TourTriggerContext";
import {
  ALLOWEAT_API_ERROR,
  ALLOWEAT_API_SERVER_ERROR,
} from "@utils/logRocket";
import VisibleNutrientsContextProvider from "@context/VisibleNutrientsContext/VisibleNutrientsContextProvider";
import { AxiosError } from "axios";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { showAppOutdatedToast } from "@Core/components/VersionToastInfo/showAppOutdatedToast";
import { ENVIRONMENT } from "@config/app";
import DToastContainer from "@UI/components/Molecules/Notification/DToastContainer";
import UnexpectedErrorMessage from "@Core/components/UnexpectedErrorMessage/UnexpectedErrorMessage";
import { applyStoredTheme } from "@UI/hooks/useTheme";

if (typeof window !== "undefined") {
  window.global = window;
}

applyStoredTheme();

const trackApiError = (status: number | undefined) => {
  if (ENVIRONMENT !== "prod" || status === undefined || status < 400) return;

  LogRocket.track(
    status >= 500 ? ALLOWEAT_API_SERVER_ERROR : ALLOWEAT_API_ERROR,
  );
};

const queryCache = new QueryCache({
  onError: (e: unknown, query) => {
    console.error(e);

    if (!window.subscriptionOK) return;

    const status = httpStatus(e);

    if (status === 403) {
      window.dispatchEvent(new Event("app:blocked"));
      return;
    }

    if (errorCode(e) === AxiosError.ERR_NETWORK) return;
    else if (status === 401) return;
    else if (query.meta?.errorHandledLocally !== true)
      toast.error(<UnexpectedErrorMessage />, { id: "error" });

    trackApiError(status);
  },
});

const mutationCache = new MutationCache({
  onError: (e: unknown, _variables, _context, mutation) => {
    console.error(e);

    const status = httpStatus(e);

    if (status === 403) {
      window.dispatchEvent(new Event("app:blocked"));
      return;
    }

    trackApiError(status);

    if (e === "Already confirmed") {
      return;
    }

    if (mutation.options.onError) return;
    toast.error(<UnexpectedErrorMessage />);
  },
});

const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error: unknown) => {
        if (errorCode(error) === AxiosError.ERR_NETWORK) return false;

        if (error instanceof ValidationError) {
          return false;
        }

        const code = errorCode(error);
        if (typeof code === "number" && code >= 400) return false;

        const status = httpStatus(error);
        if (status !== undefined && status >= 400) return false;

        return failureCount < 3;
      },
    },
  },
});

window.addEventListener("vite:preloadError", () => {
  showAppOutdatedToast();
});

createRoot(document.getElementById("root")!).render(
  <>
    <QueryClientProvider client={queryClient}>
      <TourTriggerContextProvider>
        <VisibleNutrientsContextProvider>
          <App />
        </VisibleNutrientsContextProvider>
      </TourTriggerContextProvider>
      {(ENVIRONMENT ?? "") === "local" ? (
        <ReactQueryDevtools initialIsOpen={false} />
      ) : null}
    </QueryClientProvider>
    <DToastContainer />
  </>,
);

serviceWorker.unregister();
